import React from 'react';
import { Typography, Button, Card, CardContent, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import HearingIcon from '@mui/icons-material/Hearing';
import BiotechIcon from '@mui/icons-material/Biotech';
import SpaIcon from '@mui/icons-material/Spa';
import EastIcon from '@mui/icons-material/East';
import banner from '../assets/Logo.jpg';

const services = [
  {
    title: 'Ohrakupunktur',
    icon: HearingIcon,
    description: 'Gezielte Stimulation zur Schmerzlinderung, Stressreduktion und Raucherentwöhnung.',
  },
  {
    title: 'Infusionen & Injektionen',
    icon: BiotechIcon,
    description: 'Hochdosierte Vitalstoffe zur Stärkung von Immunsystem und Wohlbefinden.',
  },
  {
    title: 'Reflexzonentherapie',
    icon: SpaIcon,
    description: 'Aktivierung der Selbstheilungskraft durch Reflexzonenmassage der Hände und Füße.',
  },
];

function WaveDivider({ flip, topColor, bottomColor }) {
  return (
    <Box sx={{ display: 'block', lineHeight: 0, transform: flip ? 'scaleY(-1)' : 'none', backgroundColor: bottomColor }}>
      <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
        <path
          d="M0,30 C240,60 480,0 720,30 C960,60 1200,0 1440,30 L1440,0 L0,0 Z"
          fill={topColor}
        />
      </svg>
    </Box>
  );
}

function Home() {
  return (
    <Box>
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(160deg, #e8f5e9 0%, #f0ede4 55%, #fdf8f0 100%)',
          py: { xs: 8, md: 14 },
          px: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative glow blobs */}
        <Box sx={{
          position: 'absolute', top: -120, right: -100, width: 450, height: 450,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(76,175,80,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -150, left: -120, width: 550, height: 550,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,168,67,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Logo ring */}
        <Box
          component="img"
          src={banner}
          alt="Heilpraktikerin Sylwia Spiller"
          sx={{
            width: { xs: 155, md: 195 },
            height: { xs: 155, md: 195 },
            borderRadius: '50%',
            objectFit: 'cover',
            border: '4px solid rgba(255,255,255,0.85)',
            boxShadow: '0 0 0 10px rgba(76,175,80,0.1), 0 12px 50px rgba(46,125,50,0.22)',
            mb: 4,
            position: 'relative',
            zIndex: 1,
          }}
        />

        <Typography
          variant="h3"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: '#1B5E20',
            mb: 2.5,
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.8rem' },
            lineHeight: 1.3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Willkommen in meiner Praxis<br />für Naturheilkunde
        </Typography>

        <Typography
          sx={{
            color: '#5C5244',
            fontSize: { xs: '1rem', md: '1.15rem' },
            maxWidth: 580,
            mx: 'auto',
            mb: 5,
            lineHeight: 1.85,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Entdecken Sie die heilende Kraft der Natur und fördern Sie Ihr
          Wohlbefinden durch ganzheitliche, individuelle Ansätze.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Button
            variant="contained"
            component={Link}
            to="/kontakt"
            size="large"
            sx={{
              borderRadius: '30px',
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              backgroundColor: '#D4A843',
              color: '#fff',
              boxShadow: '0 4px 20px rgba(212,168,67,0.4)',
              '&:hover': {
                backgroundColor: '#C49A30',
                transform: 'translateY(-2px)',
                boxShadow: '0 7px 25px rgba(212,168,67,0.5)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Jetzt Termin vereinbaren
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/leistungen"
            size="large"
            endIcon={<EastIcon fontSize="small" />}
            sx={{
              borderRadius: '30px',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderColor: '#2E7D32',
              color: '#2E7D32',
              '&:hover': {
                borderColor: '#1B5E20',
                backgroundColor: 'rgba(46,125,50,0.05)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Meine Leistungen
          </Button>
        </Box>
      </Box>

      {/* ── Wave ─────────────────────────────────────────────────── */}
      <WaveDivider topColor="#f0ede4" bottomColor="#FDFAF4" />

      {/* ── Philosophie ──────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3, backgroundColor: '#FDFAF4' }}>
        <Box sx={{ maxWidth: 740, mx: 'auto', textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontStyle: 'italic',
              color: '#D4A843',
              fontSize: '1rem',
              letterSpacing: 2,
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            Mein Ansatz
          </Typography>
          <Typography variant="h4" sx={{ color: '#1B5E20', mb: 3 }}>
            Körper, Geist & Seele im Einklang
          </Typography>

          <Card sx={{ borderRadius: '24px', border: '1px solid rgba(76,175,80,0.12)', overflow: 'hidden' }}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1B5E20, #D4A843, #4CAF50)' }} />
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography
                sx={{
                  color: '#5C5244',
                  lineHeight: 1.95,
                  fontSize: { xs: '0.97rem', md: '1.07rem' },
                  fontWeight: 300,
                }}
              >
                Ich glaube an die Verbindung von Körper, Geist und Seele. Meine
                Behandlungsmethoden sind darauf ausgerichtet, die Selbstheilungskraft
                zu aktivieren und das Wohlbefinden zu fördern. Ich begleite Sie auf
                dem Weg zu besserer Gesundheit und damit zu einer besseren Lebensqualität.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── Wave ─────────────────────────────────────────────────── */}
      <WaveDivider topColor="#FDFAF4" bottomColor="#edf7ee" />

      {/* ── Leistungen Teaser ────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #edf7ee 0%, #f0ede4 100%)',
          py: { xs: 7, md: 11 },
          px: 3,
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: '"Playfair Display", serif',
            fontStyle: 'italic',
            color: '#D4A843',
            fontSize: '1rem',
            letterSpacing: 2,
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          Behandlungen
        </Typography>
        <Typography variant="h4" sx={{ textAlign: 'center', color: '#1B5E20', mb: { xs: 5, md: 7 } }}>
          Meine Leistungen
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            justifyContent: 'center',
            maxWidth: 960,
            mx: 'auto',
          }}
        >
          {services.map(({ title, icon: Icon, description }) => (
            <Box
              key={title}
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' },
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                borderRadius: '20px',
                backgroundColor: 'rgba(255,255,255,0.78)',
                border: '1px solid rgba(76,175,80,0.12)',
                boxShadow: '0 4px 22px rgba(46,125,50,0.07)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 32px rgba(46,125,50,0.14)',
                },
              }}
            >
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5,
                  boxShadow: '0 4px 14px rgba(46,125,50,0.15)',
                }}
              >
                <Icon sx={{ fontSize: 32, color: '#2E7D32' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  color: '#1B5E20',
                  mb: 1.5,
                  fontSize: '1.05rem',
                }}
              >
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B6459', lineHeight: 1.75 }}>
                {description}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box textAlign="center" mt={6}>
          <Button
            variant="outlined"
            component={Link}
            to="/leistungen"
            endIcon={<EastIcon />}
            sx={{
              borderRadius: '30px',
              borderColor: '#2E7D32',
              color: '#2E7D32',
              px: 5,
              py: 1.3,
              fontWeight: 700,
              fontSize: '0.95rem',
              '&:hover': {
                borderColor: '#1B5E20',
                backgroundColor: 'rgba(46,125,50,0.05)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Alle Leistungen entdecken
          </Button>
        </Box>
      </Box>

      {/* ── Wave ─────────────────────────────────────────────────── */}
      <WaveDivider topColor="#f0ede4" bottomColor="#F7F3EC" />

      {/* ── CTA Banner ───────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 60%, #388E3C 100%)',
          py: { xs: 7, md: 10 },
          px: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Typography
          variant="h4"
          sx={{
            color: '#fff',
            mb: 1.5,
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          Bereit für Ihren ersten Schritt?
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, fontSize: { xs: '0.97rem', md: '1.05rem' }, lineHeight: 1.7 }}>
          Ich freue mich darauf, Sie auf Ihrem Weg zu Gesundheit und Wohlbefinden zu begleiten.
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/kontakt"
          size="large"
          sx={{
            borderRadius: '30px',
            px: 6,
            py: 1.6,
            fontSize: '1rem',
            background: '#fff',
            color: '#1B5E20',
            fontWeight: 700,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            '&:hover': {
              background: '#f0ede4',
              transform: 'translateY(-2px)',
              boxShadow: '0 7px 28px rgba(0,0,0,0.28)',
            },
            transition: 'all 0.25s ease',
          }}
        >
          Termin vereinbaren
        </Button>
      </Box>
    </Box>
  );
}

export default Home;
